import { KeyChord } from 'vscode/vscode/vs/base/common/keyCodes';
import 'vscode/vscode/vs/workbench/contrib/comments/browser/media/review.css';
import { isDiffEditor, isCodeEditor } from 'vscode/vscode/vs/editor/browser/editorBrowser';
import { registerEditorContribution } from 'vscode/vscode/vs/editor/browser/editorExtensions';
import { ICodeEditorService } from 'vscode/vscode/vs/editor/browser/services/codeEditorService';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { CommandsRegistry } from 'vscode/vscode/vs/platform/commands/common/commands';
import { KeybindingsRegistry } from 'vscode/vscode/vs/platform/keybinding/common/keybindingsRegistry';
import { ICommentService } from 'vscode/vscode/vs/workbench/contrib/comments/browser/commentService.service';
import { ctxCommentEditorFocused, SimpleCommentEditor } from 'vscode/vscode/vs/workbench/contrib/comments/browser/simpleCommentEditor';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { MenuRegistry, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { EditorContextKeys } from 'vscode/vscode/vs/editor/common/editorContextKeys';
import { ID, CommentController } from 'vscode/vscode/vs/workbench/contrib/comments/browser/commentsController';
import { Range } from 'vscode/vscode/vs/editor/common/core/range';
import { INotificationService } from 'vscode/vscode/vs/platform/notification/common/notification.service';
import { CommentContextKeys } from 'vscode/vscode/vs/workbench/contrib/comments/common/commentContextKeys';
import { CONTEXT_ACCESSIBILITY_MODE_ENABLED } from 'vscode/vscode/vs/platform/accessibility/common/accessibility';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { accessibilityHelpIsShown, accessibleViewCurrentProviderId } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibilityConfiguration';
import { registerWorkbenchContribution2 } from 'vscode/vscode/vs/workbench/common/contributions';
import { CommentsInputContentProvider } from './commentsInputContentProvider.js';

const _moduleId = "vs/workbench/contrib/comments/browser/commentsEditorContribution";
registerEditorContribution(ID, CommentController, 1 );
registerWorkbenchContribution2(CommentsInputContentProvider.ID, CommentsInputContentProvider, 2 );
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: "editor.action.nextCommentThreadAction" ,
    handler: async (accessor, args) => {
        const activeEditor = getActiveEditor(accessor);
        if (!activeEditor) {
            return Promise.resolve();
        }
        const controller = CommentController.get(activeEditor);
        if (!controller) {
            return Promise.resolve();
        }
        controller.nextCommentThread();
    },
    weight: 100 ,
    primary: 512  | 67 ,
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: "editor.action.previousCommentThreadAction" ,
    handler: async (accessor, args) => {
        const activeEditor = getActiveEditor(accessor);
        if (!activeEditor) {
            return Promise.resolve();
        }
        const controller = CommentController.get(activeEditor);
        if (!controller) {
            return Promise.resolve();
        }
        controller.previousCommentThread();
    },
    weight: 100 ,
    primary: 1024  | 512  | 67
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: "editor.action.nextCommentingRange" ,
    handler: async (accessor, args) => {
        const activeEditor = getActiveEditor(accessor);
        if (!activeEditor) {
            return Promise.resolve();
        }
        const controller = CommentController.get(activeEditor);
        if (!controller) {
            return Promise.resolve();
        }
        controller.nextCommentingRange();
    },
    when: ( (ContextKeyExpr.and(CONTEXT_ACCESSIBILITY_MODE_ENABLED,  (ContextKeyExpr.or(
        EditorContextKeys.focus,
        CommentContextKeys.commentFocused,
         (ContextKeyExpr.and(
            accessibilityHelpIsShown,
             (accessibleViewCurrentProviderId.isEqualTo("comments" ))
        ))
    ))))),
    primary: KeyChord(2048  | 41 , 2048  | 512  | 18 ),
    weight: 100
});
MenuRegistry.appendMenuItem(MenuId.CommandPalette, {
    command: {
        id: "editor.action.nextCommentingRange" ,
        title: ( localizeWithPath(_moduleId, 0, "Go to Next Commenting Range")),
        category: 'Comments',
    },
    when: CommentContextKeys.activeEditorHasCommentingRange
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: "editor.action.previousCommentingRange" ,
    handler: async (accessor, args) => {
        const activeEditor = getActiveEditor(accessor);
        if (!activeEditor) {
            return Promise.resolve();
        }
        const controller = CommentController.get(activeEditor);
        if (!controller) {
            return Promise.resolve();
        }
        controller.previousCommentingRange();
    },
    when: ( (ContextKeyExpr.and(CONTEXT_ACCESSIBILITY_MODE_ENABLED,  (ContextKeyExpr.or(
        EditorContextKeys.focus,
        CommentContextKeys.commentFocused,
         (ContextKeyExpr.and(
            accessibilityHelpIsShown,
             (accessibleViewCurrentProviderId.isEqualTo("comments" ))
        ))
    ))))),
    primary: KeyChord(2048  | 41 , 2048  | 512  | 16 ),
    weight: 100
});
MenuRegistry.appendMenuItem(MenuId.CommandPalette, {
    command: {
        id: "editor.action.previousCommentingRange" ,
        title: ( localizeWithPath(_moduleId, 1, "Go to Previous Commenting Range")),
        category: 'Comments',
    },
    when: CommentContextKeys.activeEditorHasCommentingRange
});
CommandsRegistry.registerCommand({
    id: "workbench.action.toggleCommenting" ,
    handler: (accessor) => {
        const commentService = accessor.get(ICommentService);
        const enable = commentService.isCommentingEnabled;
        commentService.enableCommenting(!enable);
    }
});
MenuRegistry.appendMenuItem(MenuId.CommandPalette, {
    command: {
        id: "workbench.action.toggleCommenting" ,
        title: ( localizeWithPath(_moduleId, 2, "Toggle Editor Commenting")),
        category: 'Comments',
    },
    when: CommentContextKeys.WorkspaceHasCommenting
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: "workbench.action.addComment" ,
    handler: async (accessor, args) => {
        const activeEditor = getActiveEditor(accessor);
        if (!activeEditor) {
            return Promise.resolve();
        }
        const controller = CommentController.get(activeEditor);
        if (!controller) {
            return Promise.resolve();
        }
        const position = args?.range ? ( (new Range(
            args.range.startLineNumber,
            args.range.startLineNumber,
            args.range.endLineNumber,
            args.range.endColumn
        )))
            : (args?.fileComment ? undefined : activeEditor.getSelection());
        const notificationService = accessor.get(INotificationService);
        try {
            await controller.addOrToggleCommentAtLine(position, undefined);
        }
        catch (e) {
            notificationService.error(( localizeWithPath(
                _moduleId,
                3,
                "The cursor must be within a commenting range to add a comment"
            )));
        }
    },
    weight: 100 ,
    primary: KeyChord(2048  | 41 , 2048  | 512  | 33 ),
});
MenuRegistry.appendMenuItem(MenuId.CommandPalette, {
    command: {
        id: "workbench.action.addComment" ,
        title: ( localizeWithPath(_moduleId, 4, "Add Comment on Current Selection")),
        category: 'Comments'
    },
    when: CommentContextKeys.activeCursorHasCommentingRange
});
CommandsRegistry.registerCommand({
    id: "workbench.action.collapseAllComments" ,
    handler: (accessor) => {
        return getActiveController(accessor)?.collapseAll();
    }
});
MenuRegistry.appendMenuItem(MenuId.CommandPalette, {
    command: {
        id: "workbench.action.collapseAllComments" ,
        title: ( localizeWithPath(_moduleId, 5, "Collapse All Comments")),
        category: 'Comments'
    },
    when: CommentContextKeys.WorkspaceHasCommenting
});
CommandsRegistry.registerCommand({
    id: "workbench.action.expandAllComments" ,
    handler: (accessor) => {
        return getActiveController(accessor)?.expandAll();
    }
});
MenuRegistry.appendMenuItem(MenuId.CommandPalette, {
    command: {
        id: "workbench.action.expandAllComments" ,
        title: ( localizeWithPath(_moduleId, 6, "Expand All Comments")),
        category: 'Comments'
    },
    when: CommentContextKeys.WorkspaceHasCommenting
});
CommandsRegistry.registerCommand({
    id: "workbench.action.expandUnresolvedComments" ,
    handler: (accessor) => {
        return getActiveController(accessor)?.expandUnresolved();
    }
});
MenuRegistry.appendMenuItem(MenuId.CommandPalette, {
    command: {
        id: "workbench.action.expandUnresolvedComments" ,
        title: ( localizeWithPath(_moduleId, 7, "Expand Unresolved Comments")),
        category: 'Comments'
    },
    when: CommentContextKeys.WorkspaceHasCommenting
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: "editor.action.submitComment" ,
    weight: 100 ,
    primary: 2048  | 3 ,
    when: ctxCommentEditorFocused,
    handler: (accessor, args) => {
        const activeCodeEditor = accessor.get(ICodeEditorService).getFocusedCodeEditor();
        if (activeCodeEditor instanceof SimpleCommentEditor) {
            activeCodeEditor.getParentThread().submitComment();
        }
    }
});
KeybindingsRegistry.registerCommandAndKeybindingRule({
    id: "workbench.action.hideComment" ,
    weight: 100 ,
    primary: 9 ,
    secondary: [1024  | 9 ],
    when: ctxCommentEditorFocused,
    handler: (accessor, args) => {
        const activeCodeEditor = accessor.get(ICodeEditorService).getFocusedCodeEditor();
        if (activeCodeEditor instanceof SimpleCommentEditor) {
            activeCodeEditor.getParentThread().collapse();
        }
    }
});
function getActiveEditor(accessor) {
    let activeTextEditorControl = accessor.get(IEditorService).activeTextEditorControl;
    if (isDiffEditor(activeTextEditorControl)) {
        if (activeTextEditorControl.getOriginalEditor().hasTextFocus()) {
            activeTextEditorControl = activeTextEditorControl.getOriginalEditor();
        }
        else {
            activeTextEditorControl = activeTextEditorControl.getModifiedEditor();
        }
    }
    if (!isCodeEditor(activeTextEditorControl) || !activeTextEditorControl.hasModel()) {
        return null;
    }
    return activeTextEditorControl;
}
function getActiveController(accessor) {
    const activeEditor = getActiveEditor(accessor);
    if (!activeEditor) {
        return undefined;
    }
    const controller = CommentController.get(activeEditor);
    if (!controller) {
        return undefined;
    }
    return controller;
}

export { getActiveEditor };
